import styled from '@emotion/styled';
import { Button, Modal } from '@material-ui/core';
import { ClassAttributes, createElement, FC, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import TiktokAdAccount from '../../../entity/TiktokAdAccount';
import { context as DependencyContext } from '../../context/dependency';
import { context as StateContext } from '../../context/state';
import IntegrateTiktokAdAccountDialog from './IntegrateTiktokAdAccountDialog';

type Props = ClassAttributes<HTMLElement> & {
  className?: string;
};

const TiktokAdAccountConfiguration: FC<Props> = ({ className }) => {
  const state = useContext(StateContext);
  const dependency = useContext(DependencyContext);
  const [linked, setLinked] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDeleteAccount, setSelectedDeleteAccount] = useState<TiktokAdAccount | null>(null);
  const tiktokAdAccounts = state.tiktokAdAccounts;
  const integratedTiktokAdAccounts = state.integratedTiktokAdAccounts.adAccounts;

  const registerToken = async () => {
    return dependency.integrateTiktokAdAccountUsecase.fetchAccessibleTiktokAdAccounts();
  };
  const onIntegrateTiktokAdAccount = async (_: any, tiktokAdAccount: TiktokAdAccount) => {
    await dependency.integrateTiktokAdAccountUsecase.integrateTiktokAdAccount(tiktokAdAccount);
  };
  const onRevokeTiktokAdAccount = (tiktokAdAccount: TiktokAdAccount) => {
    dependency.integrateTiktokAdAccountUsecase.unlinkTiktokAdAccount(tiktokAdAccount);
  };

  const onClickDeleteButton = (_: any, selected: TiktokAdAccount) => {
    setOpenModal(true);
    setSelectedDeleteAccount(selected);
  };
  const onClickDeleteSubmitButton = (_: any) => {
    if (selectedDeleteAccount !== null) {
      onRevokeTiktokAdAccount(selectedDeleteAccount);
    }
    setSelectedDeleteAccount(null);
    setOpenModal(false);
  };
  const onCancelDelete = () => {
    setSelectedDeleteAccount(null);
    setOpenModal(false);
  };

  useEffect(() => {
    async function fetchData() {
      const isTokenFetched = await registerToken();

      if (isTokenFetched && !location.href.match(/#tried/)) {
        setLinked(true);
      }
    }
    if (!location.href.match(/#tried/)) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Root className={className}>
      <LinkedAccountList>
        {integratedTiktokAdAccounts.map((account) => (
          <LinkedAccountItem key={account.id}>
            <LinkedAccountItemBody>{`${account.name}${
              account.adAccountId ? ` (${account.adAccountId})` : ''
            }`}</LinkedAccountItemBody>
            <LinkedAccountItemDelete onClick={(e) => onClickDeleteButton(e, account)}>×</LinkedAccountItemDelete>
          </LinkedAccountItem>
        ))}
      </LinkedAccountList>

      <StyledButton
        variant="contained"
        color="primary"
        onClick={() => dependency.integrateTiktokAdAccountUsecase.fetchAd()}
      >
        <FormattedMessage id="configuration_page.external_account_connect_settings.button" />
      </StyledButton>
      <IntegrateTiktokAdAccountDialog
        tiktokAdAccounts={tiktokAdAccounts}
        open={linked && tiktokAdAccounts.length !== 0}
        onRequestSendingCreative={(e, selected) => onIntegrateTiktokAdAccount(e, selected)}
        onRequestClose={() => {
          location.hash = '#tried';
          setLinked(false);
        }}
      />
      <Modal open={openModal} onClose={onCancelDelete}>
        <ModalStyle>
          <ModalTitle>
            <FormattedMessage id="configuration_page.ad_account_revoke_modal_label" />
          </ModalTitle>
          <Button color="primary" variant="contained" onClick={(e) => onClickDeleteSubmitButton(e)} fullWidth={true}>
            <FormattedMessage id="configuration_page.external_account_revoke_modal_button" />
          </Button>
        </ModalStyle>
      </Modal>
    </Root>
  );
};

const Root = styled('div')();

const LinkedAccountList = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
});

const LinkedAccountItem = styled('div')({
  display: 'flex',
  position: 'relative',
});

const LinkedAccountItemBody = styled('div')({
  display: 'flex',
  alignItems: 'center',
  fontSize: '18px',
  color: '#444',
  backgroundColor: 'white',
  border: '1px solid #ccc',
  padding: '8px 14px',
  marginRight: '8px',
  marginBottom: '8px',
  minHeight: '24px',
});

const LinkedAccountItemDelete = styled('div')({
  display: 'flex',
  position: 'absolute',
  top: '-8px',
  right: '0px',
  fontSize: '12px',
  borderRadius: '999px',
  width: '20px',
  height: '20px',
  backgroundColor: 'gray',
  color: '#ffffff',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  opacity: 0.9,
  ':hover': {
    opacity: 1,
  },
});

const ModalStyle = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '450',
  background: 'white',
  outline: 'none',
  boxShadow: '0 3px 7px rgba(0, 0, 0, 0.3)',
  borderRadius: '10px',
  border: '1px solid rgba(0, 0, 0, 0.3)',
  padding: '16px',
});

const ModalTitle = styled('div')({
  fontSize: '18px',
  color: '#222',
  margin: '32px 0px',
});

const StyledButton = styled(Button)();

export default TiktokAdAccountConfiguration;
